import { objectWithoutProperties as _objectWithoutProperties, slicedToArray as _slicedToArray, extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import * as React from 'react';
import { ModalPortal } from './ModalPortal.js';

var _excluded = ["isOpen", "onClosed"];
/**
 * Modals focus the user’s attention on a single task in a window that sits on top of the page content.
 *
 * - {@link https://livingdesign.walmart.com/components/modal/ Guidelines}
 * - {@link https://livingdesign.walmart.com/components/modal/react/ React documentation}
 *
 * ### Content
 *
 * #### Children
 *
 * The Modal accepts valid React content through the `children` prop:
 *
 * ```tsx
 * <Modal
 *   onClose={() => setIsOpen(false)}
 *   title="Title"
 * >
 *   Content
 * </Modal>
 * ```
 *
 * #### Title
 *
 * The Modal requires a title which can be provided through the `title` prop:
 *
 * ```tsx
 * <Modal
 *   onClose={() => setIsOpen(false)}
 *   title="Title"
 * >
 *   Modal
 * </Modal>
 * ```
 *
 * #### Actions
 *
 * The Modal accepts a group of one or more button-like components through the optional `actions` prop:
 *
 * ```tsx
 * <Modal
 *   actions={
 *     <ButtonGroup>
 *       <Button>Action1</Button>
 *       <Button>Action2</Button>
 *     </ButtonGroup>
 *   }
 *   onClose={() => setIsOpen(false)}
 *   title="Title"
 * >
 *   Content
 * </Modal>
 * ```
 *
 * ### Opening and closing
 *
 * The Modal is set to open or close based on the `isOpen` prop. Provide a callback handler that manages the `isOpen` state through the `onClose` prop:
 *
 * ```tsx
 * export default () => {
 *   const [isOpen, setIsOpen] = React.useState(false);
 *
 *   return (
 *     <>
 *       <Button onClick={() => setIsOpen(true)}>
 *         Open modal
 *       </Button>
 *
 *       <Modal
 *         isOpen={isOpen}
 *         onClose={() => setIsOpen(false)}
 *         title="Title"
 *       >
 *         Content
 *       </Modal>
 *     </>
 *   );
 * };
 * ```
 *
 * ### Features
 *
 * #### Size
 *
 * The Modal comes in four sizes:
 *
 * - `small` (default)
 * - `medium`
 * - `large`
 * - `mega`
 *
 * Set the size with the `size` prop:
 *
 * ```tsx
 * <Modal
 *   onClose={() => setIsOpen(false)}
 *   size="small"
 *   title="Title"
 * >
 *   Small
 * </Modal>
 *
 * <Modal
 *   onClose={() => setIsOpen(false)}
 *   size="medium"
 *   title="Title"
 * >
 *   Medium
 * </Modal>
 *
 * <Modal
 *   onClose={() => setIsOpen(false)}
 *   size="large"
 *   title="Title"
 * >
 *   Large
 * </Modal>
 * <Modal
 *   onClose={() => setIsOpen(false)}
 *   size="mega"
 *   title="Title"
 * >
 *   Mega
 * </Modal>
 * ```
 */
var Modal = function Modal(props) {
  var isOpen = props.isOpen,
    _onClosed = props.onClosed,
    rest = _objectWithoutProperties(props, _excluded);
  var _React$useState = React.useState(isOpen),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    isTransitioning = _React$useState2[0],
    setIsTransitioning = _React$useState2[1];
  var previousIsOpen = React.useRef(isOpen);

  /**
   * This hook delays `isOpen` property changes such that the
   * `ModalPortal` component can transition in and out.
   */
  React.useEffect(function () {
    if (previousIsOpen.current === isOpen) {
      return;
    }
    previousIsOpen.current = isOpen;
    var handle = setTimeout(function () {
      setIsTransitioning(true);
    }, 0);

    /* eslint-disable-next-line consistent-return */
    return function () {
      return clearTimeout(handle);
    };
  }, [isOpen]);
  if (!isOpen && !isTransitioning) {
    return null;
  }
  return /*#__PURE__*/React.createElement(ModalPortal, _extends({
    isOpen: isOpen && isTransitioning,
    onClosed: function onClosed() {
      setIsTransitioning(false);
      _onClosed === null || _onClosed === void 0 || _onClosed();
    }
  }, rest));
};
Modal.displayName = 'Modal';

export { Modal };
